<template>
  <div>
    <!-- PR-UPGRADE-MODAL: per-component premium-banner replaced by the one
         shared UpgradeToPlusModal (mounted globally in App.vue). Gating below
         is unchanged; when free AI optimizer attempts run out it opens the
         shared modal via `upgradeModal/open`. -->
    <r-dialog v-model="dialog">
      <div v-show="showAiOptimizationInProg()">
        <v-card-text class="text--primary text-body-1 pt-4 pb-3 text-center">
          <v-progress-circular indeterminate color="primary" size="48" class="mb-3 mt-2"></v-progress-circular>
          <p class="text-body-1 font-weight-medium mb-1">Optimizing...</p>
          <p class="text-body-2 font-italic text--secondary mb-0">
            This might take a few seconds to complete
          </p>
        </v-card-text>
        <v-card-actions
          :class="
            $vuetify.breakpoint.xsOnly
              ? 'flex-wrap justify-center'
              : 'flex-wrap justify-end'
          "
        >
          <v-btn outlined color="gray" text @click="dialog = false">
            Cancel
          </v-btn>
        </v-card-actions>
      </div>
      <div v-show="showAiOptimizationError()">
        <v-card-text class="text--primary text-body-1 pt-4 pb-3 text-center">
          <h3 class="pt-2">Oops. Something went wrong.</h3>
          <h3 class="text-body-1 font-italic text--secondary">
            Please try again in a few minutes.
          </h3>
        </v-card-text>
        <v-card-actions
          :class="
            $vuetify.breakpoint.xsOnly
              ? 'flex-wrap justify-center'
              : 'flex-wrap justify-end'
          "
        >
          <v-btn outlined color="gray" text @click="dialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </div>
      <div v-show="showAiOptimizedEditablePhrasing()">
        <v-card-text class="text--primary text-body-1 pt-4 pb-3">
          <h3 class="pt-2">Optimized Phrasing</h3>
          <h3 class="text-body-1 font-italic text--secondary">
            Make edits below
          </h3>
          <v-textarea v-model="aiOptimizedPhrasing" :rows="5"></v-textarea>
        </v-card-text>
        <v-card-actions
          :class="
            $vuetify.breakpoint.xsOnly
              ? 'flex-wrap justify-center'
              : 'flex-wrap justify-end'
          "
        >
          <v-btn outlined color="gray" text @click="dialog = false">
            Cancel Changes
          </v-btn>
          <v-btn outlined color="primary" text @click="saveNewPhrasing()">
            Submit and Save
          </v-btn>
        </v-card-actions>
      </div>
      <div v-show="showConfirmNewPhrasing()">
        <v-card-text class="text--primary text-body-1 pt-4 pb-3">
          <h4 class="pt-2 text-center">
            You are about to override your previously Saved Phrasing of
          </h4>
          <p class="mt-2">{{ phrasing }}</p>
          <h4 class="pt-2 text-center">With the following version:</h4>
          <p class="mt-2">{{ aiOptimizedPhrasing }}</p>
          <h4 class="pt-2 text-center">
            Do you want to override with the new version?
          </h4>
        </v-card-text>
        <v-card-actions
          :class="
            $vuetify.breakpoint.xsOnly
              ? 'flex-wrap justify-center'
              : 'flex-wrap justify-end'
          "
        >
          <v-btn color="gray" outlined text @click="backToEditing()">
            No - Continue Editing
          </v-btn>
          <v-btn
            color="primary"
            outlined
            text
            @click="overrideWithNewPhrasing()"
          >
            Yes - Override
          </v-btn>
        </v-card-actions>
      </div>
    </r-dialog>
    <div v-if="showLengthSlider" class="mt-3 mb-2">
      <div class="d-flex align-center mb-2">
        <span class="text-caption text--secondary mr-3" style="white-space: nowrap;">Length:</span>
        <v-btn-toggle v-model="lengthIndex" mandatory dense class="length-toggle" @change="onLengthChange">
          <v-btn x-small outlined>Concise</v-btn>
          <v-btn x-small outlined>Medium</v-btn>
          <v-btn x-small outlined>Detailed</v-btn>
        </v-btn-toggle>
      </div>
      <v-btn color="primary" outlined text small @click="startOptimizeWithAi()" :disabled="disabled">
        <v-icon small left>mdi-sparkles</v-icon>
        Optimize with AI
      </v-btn>
    </div>
    <v-btn v-if="!showLengthSlider" color="primary" outlined text @click="startOptimizeWithAi()" :disabled="disabled">
      <v-icon small left>mdi-sparkles</v-icon>
      Optimize with AI
    </v-btn>
  </div>
</template>
<script>
import * as log from "../utility/logging/logger";
import * as resApi from "../api/ResumeApi";
import { mapFields } from "vuex-map-fields";
import { mapActions } from "vuex";

export default {
  name: "AiOptimizePhrasing",
  props: {
    phrasing: {
      Type: String,
      default: "",
    },
    context: {
      Type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    defaultLength: {
      type: String,
      default: "short",
    },
  },
  data: () => ({
    dialog: false,
    aiOptimizationStatus: "idle",
    aiOptimizedPhrasing: "",
    lengthIndex: 0,
    lengthLabels: ["Concise", "Medium", "Detailed"],
    lengthValues: ["short", "medium", "long"],
  }),
  methods: {
    showAiOptimizationInProg() {
      return this.aiOptimizationStatus === "optimizing";
    },
    showAiOptimizedEditablePhrasing() {
      return this.aiOptimizationStatus === "optimization-ready";
    },
    showConfirmNewPhrasing() {
      return this.aiOptimizationStatus === "confirm-new-phrasing";
    },
    showAiOptimizationError() {
      return this.aiOptimizationStatus === "error";
    },
    enableAiOptimizeToggle() {
      return this.aiOptimizationStatus !== "optimizing";
    },
    onOptimizeWithAiToggle(value) {
      if (value) {
        log.logInformation("Enable optimization");
        this.optimizeWithAI();
      } else {
        log.logInformation("disable optimization");
      }
    },
    onLengthChange() {
      if (this.aiOptimizedPhrasing) {
        this.startOptimizeWithAi();
      }
    },
    startOptimizeWithAi() {
      this.dialog = true;
      log.logInformation("Enable optimization");
      this.optimizeWithAI();
    },
    saveNewPhrasing() {
      this.aiOptimizationStatus = "confirm-new-phrasing";
    },
    overrideWithNewPhrasing() {
      this.aiOptimizationStatus = "idle";
      this.dialog = false;
      this.$emit("onAcceptPhrasing", this.aiOptimizedPhrasing);
    },
    backToEditing() {
      this.aiOptimizationStatus = "optimization-ready";
    },
    async optimizeWithAI() {
      this.aiOptimizationStatus = "optimizing";
      try {
        log.logInformation("Refreshing user premium and attempts status...");

        await this.refreshPremium();

        if (!this.isPremium && this.remainingAiOptimizerAttempts <= 0) {
          this.dialog = false;
          // PR-UPGRADE-MODAL: open the one shared upgrade modal. Gating
          // (free attempts exhausted) is unchanged.
          this.$store.dispatch("upgradeModal/open", {
            featureName: "unlimited AI optimization",
            message:
              "You have used your free AI optimization attempts. Plus gives you unlimited AI optimization, along with the other AI tools and Sage coaching."
          });
        } else {
          let result = await resApi.FetchAiOptimizedPhrasing({
            resumeId: this.$store.state.resumeId,
            phrasing: this.phrasing,
            context: this.context,
            conciseness: this.lengthValues[this.lengthIndex],
          });
          if (!result.success) {
            log.logError(
              "Failed to get AI optimized description",
              "optimizeWithAI"
            );
            this.aiOptimizationStatus = "error";
          } else {
            log.logInformation("AI optimization success");
            this.aiOptimizationStatus = "optimization-ready";
            this.aiOptimizedPhrasing = result.data.optimizedPhrasing;
          }
        }
      } catch (e) {
        log.logError("Failed to get AI optimized description", e);
        this.aiOptimizationStatus = "error";
      }
    },
    ...mapActions("auth", ["refreshPremium"]),
  },
  computed: {
    showLengthSlider() {
      return this.context === "work-accomplishment" || this.context === "work-responsibility";
    },
    ...mapFields("auth", {
      isPremium: "isPremium",
      remainingAiOptimizerAttempts: "remainingAiOptimizerAttempts",
    }),
  },
  mounted() {
    var idx = this.lengthValues.indexOf(this.defaultLength);
    if (idx >= 0) this.lengthIndex = idx;
  },
};
</script>
<style scoped>
.length-toggle .v-btn {
  text-transform: none !important;
  letter-spacing: 0 !important;
  font-size: 0.75rem !important;
  min-width: 60px !important;
}
</style>